import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountExternalIdModel,
    EnterpriseCoordinatorSiteServiceModelsSitePointOfContactUpsearchModel
} from '@trialcard/apigateway.models';
import { Gender, RoleType } from '@trialcard/enums';
import { Moment } from 'moment';
import { AxiosResponse } from 'axios';
import { EdgeEnrollmentApi } from '@trialcard/apigateway.client';
import { Settings } from '~/services/settings';
import SurveyModule from '~/store/survey';
import { rolling365 } from '~/tests/e2e/@data/shared/dates';
import DocumentUploadModule from './documentUpload';
import EligibilityModule from './eligibility';
import { pharmacyMN, medicalMN } from '~/tests/e2e/@data/shared/memberNumbers';

@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public uploadModule!: DocumentUploadModule ;

    @Resolve
    public eligibilityData! : EligibilityModule ;

    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    alternativeEnrollment: boolean | null = null;
    enrollmentTypePatientOrHCP: string = '';

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null
    };

    defaultPrescriberData = [
        {
            model: {
                firstName: '',
                lastName: '',
                npi: '',
                accountRelationshipIsPrimary: true,
                prescriberLicenses: [],
                externalIds: [],
                addresses: [],
                phoneNumbers: [],
                emailAddresses: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[];

    defaultSiteData = [
        {
            model: {
                accountRelationshipIsPrimary: true,
                siteRoleType: RoleType.PrescriberSite,
                pointOfContacts: [] as EnterpriseCoordinatorSiteServiceModelsSitePointOfContactUpsearchModel[],
                description: '',
                addresses: [
                    {
                        addressOne: '',
                        addressTwo: '',
                        zip: '',
                        city: '',
                        state: '',
                    },
                ],
                contacts: [],
                emailAddresses: [],
                externalIds: [],
                phoneNumbers: [],
                globalIds: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[];

    defaultContactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];

    defaultMemberNumbers = {
        medical: medicalMN.number as string,
        pharmacy: pharmacyMN.number as string
    };

    public defaultAuthorizationData = {
        doesAgreeMarketing: false as boolean,
    };
    currentDrugNamePath = '';

    defaultExpirationDate = rolling365
    defaultExternalIds = [] as EnterpriseCoordinatorAccountServiceModelsAccountExternalIdModel[];

    pharmaMemberNumber = this.defaultMemberNumbers.pharmacy;
    medicalMemberNumber = this.defaultMemberNumbers.medical;

    expirationDate = this.defaultExpirationDate;
    patientInformationData = { ...this.defaultPatientInformationData };
    prescriberData = [ ...this.defaultPrescriberData ];
    siteData = [ ...this.defaultSiteData ];
    externalIds = [ ...this.defaultExternalIds ];
    AuthorizationData = { ...this.defaultAuthorizationData };


    @Mutation
    updateAuthorizationData(newAuthorizationData: typeof EnrollmentModule.prototype.defaultAuthorizationData) {
        this.AuthorizationData = { ...newAuthorizationData };
    }

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    public async updatePrescribers(newPrescriberData: UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[]) {
        this.prescriberData = [...newPrescriberData];
    }

    @Mutation
    public async updateSites(newSites: UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[]) {
        this.siteData = [...newSites];
    }

    @Mutation
    public async updateExternalIds(externalId: EnterpriseCoordinatorAccountServiceModelsAccountExternalIdModel[]) {
        this.externalIds = [ ...externalId ];
    }

    @Mutation
    public setEnrollmentType(alternative: boolean) {
        this.alternativeEnrollment = alternative;
    }

    @Mutation
    public setEnrollmentTypePatientOrHCP(alternative: string) {
        this.enrollmentTypePatientOrHCP = alternative;
        localStorage.setItem('enrollmentTypePatientOrHCP', alternative)
    }

    @Mutation
    public setDrugNamePath(value: string) {
        this.currentDrugNamePath = value;
        localStorage.setItem('drugNamePath', value);
    }


    @Mutation
    public setPharmaMemberNumber(pharmaMemberNumber: string) {
        this.pharmaMemberNumber = pharmaMemberNumber;
    }

    @Mutation
    public setMedicalMemberNumber(medicalMemberNumber: string) {
        this.medicalMemberNumber = medicalMemberNumber;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }

    @Action({ rawError: true })
    public clearStore() {
        this.eligibilityData.updateEligibilityData([]);
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
        this.setExpirationDate(this.defaultExpirationDate);
        this.updatePatientInformation(this.defaultPatientInformationData);
        this.updatePrescribers(this.defaultPrescriberData);
        this.updateSites(this.defaultSiteData);
        this.uploadModule.clearFiles();
        localStorage.removeItem('drugNamePath');
        this.updateAuthorizationData(this.defaultAuthorizationData);
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
        this.updateAuthorizationData(this.defaultAuthorizationData);
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        let response = null;
        const { prescriberData, siteData, externalIds } = this;
        try {
            const patient = {
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            externalIds
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    prescriberData,
                    siteData,
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
            } as EnrollPatientRequestModel;
            response = await this.patientEnrollmentApi.patientEnrollmentEnrollPatient(patient, this.settings.programId);
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }
}
